import { cloneDeep, isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { HeaderBidderActiveActive } from '~/src/dash/api/console-api';
import {
  DASHBOARD_GRAPH_AUCTIONS,
  DASHBOARD_GRAPH_BUYERS,
  getDashboardGraphID,
  getItemsKey,
  getUpdatedEndDate,
  IDashboardChartData,
  IDashboardHomeCardMetrics,
  IDashboardOverviewData,
  queryHomeDashboardData,
  updateCompareEndDate,
} from '~/src/dash/api/dashboards/home';
import { querySmallBidders } from '~/src/dash/api/filters/bidders';
import { getFilteredDemandSourcesList } from '~/src/dash/api/filters/demandSources';
import { MobileCards } from '~/src/dash/components/cards/MobileCards';
import { IDashboardChartMetric, mergeCompareData } from '~/src/dash/components/charts/common';
import { MobileCompareChart } from '~/src/dash/components/charts/MobileCompareChart';
import { MobileNativeSelect } from '~/src/dash/components/dropdowns/MobileNativeSelect';
import {
  FILTER_PILLS,
  FilterPillKey,
  formatSelectedChannelGroupIntoStringOfIDs,
  getMobileFilterParams,
} from '~/src/dash/components/filters/common';
import { CalendarIcon, MobileFilterIcon } from '~/src/dash/components/icons/misc-icons';
import { DensityRangeHourByHour } from '~/src/dash/globals/constants';
import {
  Col,
  Cols,
  Div,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  Text,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  areDateRangesMatchingByDay,
  formatDateEndDay,
  formatDateStartDay,
  isDateTodayWithTimezone,
} from '~/src/dash/utils/date';
import { EDateGroup } from '~/src/dash/utils/dateGroups';
import { DateRange, findDateRange, generateQueryDates } from '~/src/dash/utils/dateTimeRanges';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { backgroundColor } from '~/src/dash/utils/material-dashboard-react';
import { checkUserInfoMasked } from '~/src/dash/utils/roles';
import { getTimezone } from '~/src/dash/utils/timezone';
import {
  clearedActiveFilters,
  extractViableMetrics,
  updateDefaultSelectedForFilters,
  updateDisplayedMetricKeys,
} from '~/src/dash/views/dashboards/DashboardHome/common/common';
import { useMobileConfig, useMobileStore } from '~/src/dash/views/mobile/contexts';
import { EDashboardTab } from '~/src/dash/views/mobile/MobileBaseView/config';
import {
  DateGroupSelector,
  EMobileFilter,
  isAnyFilterActive,
} from '~/src/dash/views/mobile/MobileBaseView/Home/common';
import {
  IMobileDashboardLiveConfig,
  MobileDashboardLiveCardGroup,
} from '~/src/dash/views/mobile/MobileBaseView/Home/configs/liveConfig';
import { IMobileDashboardOverviewConfig } from '~/src/dash/views/mobile/MobileBaseView/Home/configs/overviewConfig';
import { useMobileDashboardStore } from '~/src/dash/views/mobile/MobileBaseView/Home/contexts';
import { FilterModal } from '~/src/dash/views/mobile/MobileBaseView/Home/FilterModal';
import {
  FilterSelector,
  IMobileFilterSelection,
} from '~/src/dash/views/mobile/MobileBaseView/Home/FilterSelector';
import { MobileDateRangeSelector } from '~/src/dash/views/mobile/MobileBaseView/Home/MobileDateRangeSelector';
import { MobileFiltersRow } from '~/src/dash/views/mobile/MobileBaseView/Home/MobileFiltersRow';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

const mobileFilterSelections = [
  // we start with 1 because 0 will represent none to the component
  { label: 'Filters', value: 1, SVGIcon: MobileFilterIcon },
  { label: 'Calendar', value: 2, SVGIcon: CalendarIcon },
] as IMobileFilterSelection[];

export const MobileDashboardOverview = () => {
  const { Dashboards } = useMobileConfig();
  const { session, user, currencyType, isSuperUser } = useMobileStore();
  const { t } = usePublicaTranslation();
  const defaultTimezone = getTimezone(user.user.Timezone, session.publisher.Timezone);
  const cfg = Dashboards.Overview;
  const liveCfg = Dashboards.Live;
  const { cards, defaultState } = cfg;
  const { tabs, handleChangeTab } = useMobileDashboardStore();
  const initialQueryDates = generateQueryDates(defaultState.dateRange, defaultTimezone);

  const initialLoadStart = moment().unix();
  const isInitialized = useRef(false);
  const isMounted = useRef(false);
  const [loadState, setLoadState] = useState(LoadingStateLoading);

  // filters
  const [filtersLoadState, setFiltersLoadState] = useState(LoadingStateLoading);
  const [filters, setFilters] = useState({} as Record<string, IMenuList>);
  const [selectedFilters, setSelectedFilters] = useState({} as Record<string, IMenuList>);

  // filter modal
  const [openFilter, setOpenFilter] = useState('');
  const [filterModalInfo, setFilterModalInfo] = useState({} as Record<string, IListItem>);

  // active filters
  const [activeFilters, setActiveFilters] = useState(defaultState.activeFilters);

  // snackbar
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  const [graphID, setGraphID] = useState(getDashboardGraphID(defaultState.activeFilters));

  const isCompareEnabled = defaultState.isCompareEnabled;
  const [grouping, setGrouping] = useState(defaultState.grouping);

  const [activeFilterSection, setActiveFilterSection] = useState(
    isAnyFilterActive(defaultState.activeFilters) ? EMobileFilter.Filters : EMobileFilter.None,
  );

  // calendar
  const timezone = defaultTimezone;
  const [dateRange, setDateRange] = useState(defaultState.dateRange);
  const [startDate, setStartDate] = useState(initialQueryDates.startDate);
  const [endDate, setEndDate] = useState(initialQueryDates.endDate);
  const [compareStartDate, setCompareStartDate] = useState(initialQueryDates.compareStartDate);
  const [compareEndDate, setCompareEndDate] = useState(initialQueryDates.compareEndDate);

  const [isLiveDataEnabled, setIsLiveDataEnabled] = useState(true);

  const [displayedMetricKeys, setDisplayedMetricKeys] = useState(defaultState.displayedMetricKeys);
  const [activeChartMetrics, setActiveChartMetrics] = useState(defaultState.activeChartMetrics);

  const [data, setData] = useState({} as IDashboardOverviewData);

  const isClearAllDisplayed = Object.values(selectedFilters).some((filter) => {
    return !isEmpty(filter);
  });

  // first we need to make sure all the filters are loaded
  useEffect(() => {
    getFilters();
    track('Mobile Dash Overview - View', { isSuperUser }, user.user);
  }, []);

  // whenever the date ranges change we need to update these specific query params
  useEffect(() => {
    if (isMounted.current) {
      const queryDates = generateQueryDates(dateRange, timezone);
      const { defaultDateGroup } = findDateRange(dateRange);
      unstable_batchedUpdates(() => {
        setStartDate(queryDates.startDate);
        setEndDate(queryDates.endDate);
        setCompareStartDate(queryDates.compareStartDate);
        setCompareEndDate(queryDates.compareEndDate);
        setGrouping(defaultDateGroup);
      });
      defaultState.grouping = defaultDateGroup;
    }
  }, [dateRange, timezone]);

  // run the api call when any of these states change
  useEffect(() => {
    if (isMounted.current && filtersLoadState === LoadingStateHasData) {
      getHomeDashboardData();
    }
  }, [
    filtersLoadState,
    selectedFilters,
    grouping,
    startDate,
    endDate,
    compareStartDate,
    compareEndDate,
    selectedFilters,
  ]);

  // update the config default selected for filters
  useEffect(() => {
    if (isMounted.current) {
      updateDefaultSelectedForFilters(selectedFilters, cfg.filters, cfg.defaultState);
    }
  }, [selectedFilters]);

  useEffect(() => {
    isMounted.current = true;
  }, []);

  const getHomeDashboardData = () => {
    setLoadState(LoadingStateLoading);
    const isEndDateToday = isDateTodayWithTimezone(endDate, timezone);
    // date checks
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    void getUpdatedEndDate(graphID, endDate, session.publisher.ID, timezone, t).then(
      ({ formattedEndDate, errorMessage }) => {
        if (errorMessage) {
          unstable_batchedUpdates(() => {
            setIsLiveDataEnabled(false);
            setSnackbarError(false);
            setSnackbarMessage(errorMessage);
          });
        }
        // update compare end date if needed
        const updatedCompareEndDate =
          isCompareEnabled &&
          isEndDateToday &&
          areDateRangesMatchingByDay(startDate, endDate, compareStartDate, compareEndDate)
            ? updateCompareEndDate(compareEndDate, timezone)
            : formatDateEndDay(compareEndDate);

        // create filter params object
        const filterParams = getMobileFilterParams(selectedFilters, cfg.filters, activeFilters);

        const liveItemsKey = getItemsKey(activeFilters);
        const updatedGraphID = activeFilters.demand
          ? DASHBOARD_GRAPH_BUYERS
          : DASHBOARD_GRAPH_AUCTIONS;

        const query = {
          publisher_id: session.publisher.ID,
          graph_id: updatedGraphID,
          timezone: session.publisher.Timezone,
          start_date: formatDateStartDay(startDate),
          end_date: formattedEndDate,
          is_compare_enabled: isCompareEnabled,
          compare_start_date: formatDateStartDay(compareStartDate),
          compare_end_date: updatedCompareEndDate,
          grouping,
          ...filterParams,
          // misc
          currency_type: currencyType,
          // live
          is_live_data_enabled: isLiveDataEnabled && isEndDateToday,
          is_live_data_to_be_merged: true,
          is_live_refresh_only: false,
          live_density_range: DensityRangeHourByHour,
          live_items_key: liveItemsKey,
          // live publica specific filters
          publica_auction_requesters: '',
          publica_devicetypes: '',
          publica_biddernames: '',
          publica_clusters: '',
          publica_regions: '',
          publica_publisher_ids: '',
        };
        queryHomeDashboardData(session.publisher.ID, query)
          .then((result) => {
            unstable_batchedUpdates(() => {
              setData(result);
              setLoadState(LoadingStateHasData);
              // if the graph id has changed, we must update the displayed cards for both overview and live
              if (graphID !== updatedGraphID) {
                const updatedDisplayedMetricKeys = updateDisplayedMetricKeys(
                  cards.groupings[updatedGraphID],
                  displayedMetricKeys,
                );
                const updatedActiveChartMetrics = [] as IDashboardChartMetric[];
                activeChartMetrics.forEach((metric) => {
                  const metricKey = metric.key as keyof IDashboardHomeCardMetrics;
                  if (updatedDisplayedMetricKeys.includes(metricKey)) {
                    updatedActiveChartMetrics.push(metric);
                  }
                  if (isEmpty(updatedActiveChartMetrics)) {
                    const newMetricKey = updatedDisplayedMetricKeys[0];
                    const metric = cfg.cards.metrics[newMetricKey];
                    if (!isEmpty(metric)) {
                      const { label, valueType, chartType } = metric;
                      updatedActiveChartMetrics.push({
                        key: newMetricKey,
                        label,
                        valueType,
                        chartColorIndex: 0,
                        chartType,
                      });
                    }
                  }
                });
                unstable_batchedUpdates(() => {
                  setDisplayedMetricKeys(updatedDisplayedMetricKeys);
                  setGraphID(updatedGraphID);
                  setActiveChartMetrics(updatedActiveChartMetrics);
                });
                // update config default state
                defaultState.displayedMetricKeys = updatedDisplayedMetricKeys;
                defaultState.activeChartMetrics = updatedActiveChartMetrics;
                // update live config default state
                const liveCardGroup: MobileDashboardLiveCardGroup =
                  updatedGraphID === DASHBOARD_GRAPH_BUYERS ? 'demandSelected' : 'noDemandSelected';
                const updatedDisplayedLiveMetricKeys = updateDisplayedMetricKeys(
                  liveCfg.cards.groupings[liveCardGroup],
                  displayedMetricKeys,
                );
                liveCfg.defaultState.displayedMetricKeys = updatedDisplayedLiveMetricKeys;
                liveCfg.defaultState.activeChartMetrics = updatedActiveChartMetrics;

                if (!isInitialized.current) {
                  track(
                    'Mobile Dash Overview - Initial Data Load Time',
                    { isSuperUser, initialLoadTime: moment().unix() - initialLoadStart },
                    user.user,
                  );
                  isInitialized.current = true;
                }
              }
            });
          })
          .catch(() => {
            unstable_batchedUpdates(() => {
              setData({
                MainData: [] as IDashboardChartData[],
                CompareData: [] as IDashboardChartData[],
                CardMetrics: {} as IDashboardHomeCardMetrics,
              });
              setLoadState(LoadingStateNoData);
              setSnackbarError(true);
              setSnackbarMessage('Error retrieving data. Please wait and try again.');
            });
          });
      },
    );
  };

  // load all filters
  const getFilters = () => {
    setFiltersLoadState(LoadingStateLoading);
    let updatedFilters = {} as Record<FilterPillKey, IMenuList>;
    const updatedSelectedFilters = {} as Record<FilterPillKey, IMenuList>;
    const apiCalls = [] as Array<Promise<Record<string, IMenuList>>>;

    const filterKeys = cfg.orderedFilterKeys as FilterPillKey[];

    filterKeys.forEach((key) => {
      const { list, getList } = FILTER_PILLS[key];
      // if the filter requires an api call, load it
      if (getList) {
        apiCalls.push(getList(session, user));
      } else if (list) {
        updatedFilters[key] = FILTER_PILLS[key].list || ({} as IMenuList);
      }
    });
    Promise.all(apiCalls).then((apiFilters) => {
      for (const filter of apiFilters) {
        updatedFilters = { ...updatedFilters, ...filter };
      }
      // if there are selected defaults, make them selected true here before continuing
      filterKeys.forEach((key) => {
        if (cfg.filters[key].defaultSelected && !isEmpty(cfg.filters[key].defaultSelected)) {
          const defaultSelected = cfg.filters[key].defaultSelected || [];
          defaultSelected.forEach((id) => {
            updatedFilters[key][id].selected = true;
            if (updatedSelectedFilters[key]) {
              updatedSelectedFilters[key][id] = updatedFilters[key][id];
            } else {
              updatedSelectedFilters[key] = {
                [id]: updatedFilters[key][id],
              };
            }
          });
        }
      });
      unstable_batchedUpdates(() => {
        setFilters(updatedFilters);
        setSelectedFilters(updatedSelectedFilters);
        setFiltersLoadState(LoadingStateHasData);
      });
    });
  };

  // this updates the bidders filter according to site ids
  const refreshBiddersFilter = async (siteIDs: string) => {
    const result = await querySmallBidders(session.publisher.ID, {
      publisher_id: session.publisher.ID,
      site_ids: siteIDs,
    });
    const biddersList: IMenuList = {};
    result.HeaderBidders.forEach((hb) => {
      const ID = hb.ID;
      const title = checkUserInfoMasked(user.user) ? `demo_bidder_${ID}` : hb.Name;
      biddersList[ID] = {
        title,
        selected: false,
        ID,
        active: hb.Active === HeaderBidderActiveActive,
      };
    });
    const refreshedSelectedBidders = {} as IMenuList;
    if (!isEmpty(selectedFilters.Bidders)) {
      Object.keys(selectedFilters.Bidders).forEach((id) => {
        if (biddersList[id]) {
          biddersList[id].selected = true;
          refreshedSelectedBidders[id] = biddersList[id];
        }
      });
    }
    return {
      selectedBidders: refreshedSelectedBidders,
      allBidders: biddersList,
    };
  };

  // this updates the demand sources filter according to site ids
  const refreshDemandSourcesFilter = async (siteIDs: string) => {
    const result = await getFilteredDemandSourcesList(session, user, siteIDs);
    const filteredDemandSources = result['Filtered Demand Sources'];
    const refreshedSelectedDemandSources = {} as IMenuList;
    if (!isEmpty(selectedFilters['Filtered Demand Sources'])) {
      Object.keys(selectedFilters['Filtered Demand Sources']).forEach((id) => {
        if (filteredDemandSources[id]) {
          filteredDemandSources[id].selected = true;
          refreshedSelectedDemandSources[id] = filteredDemandSources[id];
        }
      });
    }
    return {
      selectedDemandSources: refreshedSelectedDemandSources,
      allDemandSources: filteredDemandSources,
    };
  };

  const handleOpenFilter = (filterKey: string) => {
    unstable_batchedUpdates(() => {
      setOpenFilter(filterKey);
      setFilterModalInfo(filters[filterKey]);
    });
  };

  const handleCloseFilter = () => {
    unstable_batchedUpdates(() => {
      setOpenFilter('');
      setFilterModalInfo({} as Record<string, IListItem>);
    });
  };

  const handleAcceptFilter = async (
    key: string,
    allOptions: Record<string, IListItem>,
    selectedOptions: Record<string, IListItem>,
  ) => {
    const appliedFilter = cfg.filters[key];
    const { category } = appliedFilter;
    const clonedActiveFilters = cloneDeep(activeFilters);
    if (!selectedFilters[key] && isEmpty(selectedOptions)) {
      appliedFilter.defaultSelected = [];
    } else if (!!selectedFilters[key] && isEmpty(selectedOptions)) {
      appliedFilter.defaultSelected = [];
      if (category && clonedActiveFilters[category] === key) {
        clonedActiveFilters[category] = '';
      }
    } else {
      appliedFilter.defaultSelected = Object.values(selectedOptions).map((item) => item.ID);
      if (category) clonedActiveFilters[category] = key;
    }

    // custom block for refreshing bidders & demand sources
    const updatedSelectedFilters = {} as Record<string, IMenuList>;
    const updatedFilters = {} as Record<string, IMenuList>;
    if (category === 'supply') {
      let siteIDs = '';
      if (key === 'Channels') {
        siteIDs = Object.keys(selectedOptions).join(',');
      }
      if (key === 'Channel Groups') {
        siteIDs = formatSelectedChannelGroupIntoStringOfIDs(selectedOptions);
      }
      const results = await Promise.all([
        refreshBiddersFilter(siteIDs),
        refreshDemandSourcesFilter(siteIDs),
      ]);
      const { selectedBidders, allBidders } = results[0];
      const { selectedDemandSources, allDemandSources } = results[1];
      updatedSelectedFilters.Bidders = selectedBidders;
      updatedFilters.Bidders = allBidders;
      if (isEmpty(selectedBidders) && activeFilters.demand === 'Bidders') {
        clonedActiveFilters.demand = '';
      }
      updatedSelectedFilters['Filtered Demand Sources'] = selectedDemandSources;
      updatedFilters['Filtered Demand Sources'] = allDemandSources;
      if (isEmpty(selectedDemandSources) && activeFilters.demand === 'Filtered Demand Sources') {
        clonedActiveFilters.demand = '';
      }
    }

    // update states
    unstable_batchedUpdates(() => {
      setFilters({ ...filters, ...{ [key]: allOptions }, ...updatedFilters });
      setSelectedFilters((selectedFilters) => ({
        ...selectedFilters,
        ...{ [key]: selectedOptions },
        ...updatedSelectedFilters,
      }));
      setActiveFilters(clonedActiveFilters);
      setOpenFilter('');
      setFilterModalInfo({} as Record<string, IListItem>);
    });
    defaultState.activeFilters = clonedActiveFilters;

    track('Mobile Dash Overview - Use Filter', { isSuperUser, filter: key }, user.user);
  };

  const handleClearAllFilters = () => {
    const selectedFilterKeys = Object.keys(selectedFilters);
    selectedFilterKeys.forEach((filterKey) => {
      const selectedItemKeys = Object.keys(selectedFilters[filterKey]);
      selectedItemKeys.forEach((key) => {
        filters[filterKey][key].selected = false;
      });
    });
    unstable_batchedUpdates(() => {
      setSelectedFilters({});
      setActiveFilters(clearedActiveFilters);
    });
    defaultState.activeFilters = clearedActiveFilters;
  };

  const handleSelectDateRange = (selectedDateRange: DateRange) => {
    setDateRange(selectedDateRange);
    defaultState.dateRange = selectedDateRange;
    track(
      'Mobile Dash Overview - Select Date Range',
      { isSuperUser, dateRange: selectedDateRange },
      user.user,
    );
  };

  const handleSelectDateGroup = (selectedDateGroup: number) => {
    setGrouping(selectedDateGroup);
    defaultState.grouping = selectedDateGroup;
    track(
      'Mobile Dash Overview - Select Date Group',
      { isSuperUser, dateGroup: EDateGroup[selectedDateGroup] },
      user.user,
    );
  };

  const handleOnChangeCardMetric = (
    metricKey: keyof IDashboardHomeCardMetrics,
    index: number,
    isDisplayedOnChart: boolean = false,
  ) => {
    const clonedDisplayedMetricKeys = [...displayedMetricKeys];
    clonedDisplayedMetricKeys[index] = metricKey as keyof IDashboardHomeCardMetrics;
    setDisplayedMetricKeys(clonedDisplayedMetricKeys);
    defaultState.displayedMetricKeys = clonedDisplayedMetricKeys;
    track(
      'Mobile Dash Overview - Change Card Metric',
      { isSuperUser, metric: metricKey },
      user.user,
    );
    if (isDisplayedOnChart) {
      handleOnClickCard(metricKey, index);
    }
  };

  // we set this as an array to prepare for multi-metric charts in v2
  // for now we only have one metric active
  const handleOnClickCard = (
    metricKey: keyof IDashboardHomeCardMetrics,
    cardIndex: number,
  ): void => {
    const filteredMetrics = activeChartMetrics.filter((metric) => metric.key === metricKey);
    if (isEmpty(filteredMetrics)) {
      const selectedMetric = cfg.cards.metrics[metricKey];
      if (!isEmpty(selectedMetric)) {
        const updatedActiveMetrics = {
          key: metricKey,
          label: selectedMetric.label,
          valueType: selectedMetric.valueType,
          chartColorIndex: cardIndex,
          chartType: selectedMetric.chartType,
        };
        setActiveChartMetrics([updatedActiveMetrics]);
        defaultState.activeChartMetrics = [updatedActiveMetrics];
        track(
          'Mobile Dash Overview - Display Metric On Chart',
          { isSuperUser, metric: metricKey },
          user.user,
        );
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  return (
    <Panels max vertical relative>
      {/* DASHBOARD SELECT AND FILTERS */}
      <Panel
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor,
          zIndex: 2,
        }}
      >
        <Rows>
          <Row noPadding style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
            <Cols
              horizontalPadding
              smallVerticalPadding
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Col>
                <MobileNativeSelect
                  defaultValue={EDashboardTab.Overview}
                  options={tabs}
                  handleChange={handleChangeTab}
                  size='large'
                  style={{ minWidth: '128px' }}
                />
              </Col>
              {/* BUTTONS */}
              <Col>
                <FilterSelector
                  activeOption={activeFilterSection}
                  options={mobileFilterSelections}
                  handleClick={setActiveFilterSection}
                />
              </Col>
            </Cols>
          </Row>
          {/* FILTERS */}
          <Row
            noPadding
            style={{
              display: activeFilterSection === EMobileFilter.Filters ? 'flex' : 'none',
              borderBottom: `1px solid ${publicaColors.lightGray}`,
            }}
          >
            <Div horizontalPadding smallVerticalPadding style={{ overflow: 'hidden' }}>
              <MobileFiltersRow
                cfg={cfg.filters}
                handleOpenFilter={handleOpenFilter}
                activeFilters={activeFilters}
                handleClearAll={handleClearAllFilters}
                enableClearAll={isClearAllDisplayed}
              />
            </Div>
          </Row>
          {/* CALENDAR */}
          <Row
            noPadding
            style={{
              display: activeFilterSection === EMobileFilter.Calendar ? 'flex' : 'none',
              borderBottom: `1px solid ${publicaColors.lightGray}`,
            }}
          >
            <Div horizontalPadding smallVerticalPadding style={{ overflow: 'hidden' }}>
              <MobileDateRangeSelector
                selectedRange={dateRange}
                handleClick={handleSelectDateRange}
              />
            </Div>
          </Row>
        </Rows>
      </Panel>
      <Panel>
        <Rows>
          <Row smallVerticalPadding>
            <Text small bold>
              Top KPIs
            </Text>
          </Row>
          <Row noVerticalPadding>
            <MobileCards
              availableMetrics={extractViableMetrics(cards, graphID)}
              displayedMetricKeys={displayedMetricKeys}
              cardMetrics={data.CardMetrics}
              loadState={loadState}
              isCompareEnabled={isCompareEnabled}
              activeChartMetrics={activeChartMetrics}
              handleOnClickCard={handleOnClickCard}
              handleOnChangeCardMetric={handleOnChangeCardMetric}
            />
          </Row>
          <Row paddingBottom='0px'>
            <Text bold style={{ textAlign: 'center' }}>
              {activeChartMetrics[0].label}
            </Text>
          </Row>
          <Row>
            <MobileCompareChart
              loadState={loadState}
              isCompareEnabled={isCompareEnabled}
              data={mergeCompareData(data.MainData, data.CompareData, isCompareEnabled, grouping)}
              metrics={activeChartMetrics}
              metricsConfig={cards.metrics}
              grouping={grouping}
            />
          </Row>
          <Row
            noVerticalPadding
            style={{
              justifyContent: 'center',
              alignContent: 'center',
            }}
          >
            <DateGroupSelector
              activeGrouping={grouping}
              activeDateRange={dateRange}
              handleSelectGrouping={handleSelectDateGroup}
            />
          </Row>
        </Rows>
      </Panel>
      <FilterModal
        open={!!openFilter}
        handleClose={handleCloseFilter}
        filterKey={openFilter}
        options={filterModalInfo}
        handleAccept={handleAcceptFilter}
        selectedLimit={(openFilter && cfg.filters[openFilter].limit) || undefined}
        displayID={(openFilter && cfg.filters[openFilter].displayID) || false}
        currentSelected={selectedFilters[openFilter]}
      />
      <SnackbarView
        autoHideDuration={12000}
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={handleSnackbarClose}
        messageColor=''
        backgroundColor=''
        closeColor=''
      >
        {snackbarMessage}
      </SnackbarView>
      <PanelBreak height='40px' />
    </Panels>
  );
};
